import { Handshake, Landmark, TrendingUp, Users, ArrowRight, Check } from "lucide-react";
import { MigrationChecklist } from "./MigrationChecklist";

const INK = "#0F1B2D";
const YELLOW = "#E0663F";
const AMBER = "#0E509C";

const PHASES = [
  {
    Icon: Handshake, tag: "Phase 1 · Migrate clean",
    t: "Off your current vendor without losing a record",
    points: [
      "Import personal-trading history, attestations, ADV versions, and policies — nothing re-keyed by hand",
      "Compliance manual, Code of Ethics, and risk assessment refreshed against today's SEC exam priorities",
      "Run in parallel with your current platform until you're comfortable — then cut over before renewal",
    ],
  },
  {
    Icon: Landmark, tag: "Phase 2 · Keep the SEC happy",
    t: "The ongoing program, run like clockwork",
    points: [
      "Quarterly transaction reports, annual holdings, pre-clearance — tracked, chased, and evidenced",
      "Marketing Rule reviews, Form ADV / CRS amendments, and the 206(4)-7 annual review on the calendar",
      "ERISA / 401(k) plan-committee oversight and 408(b)(2) disclosures documented, not assumed",
      "Exam document requests answered from an export — not a three-week project",
    ],
  },
  {
    Icon: TrendingUp, tag: "Phase 3 · Grow & scale",
    t: "A program that scales with the firm",
    points: [
      "New services, trust relationships, or an acquisition → risk assessment and policies refreshed, not rebuilt",
      "Fractional hours flex up around exams and filings, down after",
      "FinCEN's 2028 RIA AML rule — the BSA program is ready before the deadline, not after",
    ],
  },
];

const GUIDE = [
  {
    n: "1", t: "Map what you have today",
    b: "We inventory your current vendor's data, your manual, and your calendar — and hand you a prioritized list of what moves, what improves, and what you've been living without.",
  },
  {
    n: "2", t: "Prototype alongside",
    b: "Your dedicated expert configures the prototype to RTD's workflows in ~2 weeks. Your team clicks through it live — with real (redacted) records — before anyone signs anything.",
  },
  {
    n: "3", t: "Cut over before renewal",
    b: "Go live within 45 days of kickoff, ahead of the 12/31–1/31 window. Either way, the platform and every record stay with you — you own the data and the keys.",
  },
];

export function DueDiligenceGuide() {
  return (
    <section id="diligence" className="anchor-target py-14 md:py-20" data-testid="diligence-section">
      <span className="rt-eyebrow">Compliance &amp; legal · from migration to scale</span>
      <h2 className="rt-h text-4xl md:text-5xl mt-3 max-w-3xl" data-testid="diligence-headline">
        Migrate clean. Keep the SEC happy. <span className="rt-grad-text">Grow without the growing pains.</span>
      </h2>
      <p className="mt-5 text-lg leading-relaxed max-w-3xl" style={{ color: INK, opacity: 0.78 }}>
        An adviser managing wealth, trusts, nonprofit portfolios, and 401(k) plans since 1983 has four decades of
        records and a compliance calendar that never stops. We handle the compliance and legal work at every
        stage: getting off a platform that stopped improving, keeping examiners confident, and scaling the
        program as RTD Financial grows.
      </p>

      <div className="mt-9 grid grid-cols-1 md:grid-cols-3 gap-4">
        {PHASES.map(({ Icon, tag, t, points }, i) => (
          <div key={tag} className="rt-card p-6 flex flex-col" data-testid={`phase-card-${i}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="h-10 w-10 rounded-xl grid place-items-center" style={{ background: `${YELLOW}33` }}>
                <Icon className="h-5 w-5" style={{ color: AMBER }} strokeWidth={1.8} />
              </div>
              <span className="text-[10px] font-extrabold uppercase tracking-[0.14em]" style={{ color: AMBER }}>{tag}</span>
            </div>
            <div className="rt-h text-lg mb-3" style={{ color: INK }}>{t}</div>
            <ul className="space-y-2 flex-1">
              {points.map((p) => (
                <li key={p} className="flex items-start gap-2 text-[13.5px] leading-relaxed" style={{ color: INK, opacity: 0.75 }}>
                  <Check className="h-3.5 w-3.5 mt-1 flex-shrink-0" style={{ color: AMBER }} strokeWidth={3} />
                  {p}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="mt-10 rt-card p-6 md:p-8" style={{ borderColor: `${AMBER}40` }} data-testid="diligence-guide">
        <div className="flex items-center gap-2 mb-1">
          <Users className="h-4 w-4" style={{ color: AMBER }} />
          <span className="rt-eyebrow">The guide · switching vendors ahead of renewal</span>
        </div>
        <h3 className="rt-h text-2xl md:text-3xl mt-2 max-w-2xl" style={{ color: INK }}>
          Map it. Prototype alongside. Cut over before renewal.
        </h3>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          {GUIDE.map(({ n, t, b }) => (
            <div key={n} className="rounded-2xl p-5 relative" style={{ background: `${YELLOW}14`, border: `1px solid ${YELLOW}66` }} data-testid={`guide-step-${n}`}>
              <div className="absolute top-3 right-4 rt-h text-4xl" style={{ color: `${AMBER}30` }}>{n}</div>
              <div className="rt-h text-base mb-1.5 pr-8" style={{ color: INK }}>{t}</div>
              <p className="text-[13px] leading-relaxed" style={{ color: INK, opacity: 0.72 }}>{b}</p>
            </div>
          ))}
        </div>
        <MigrationChecklist />
        <a href="#build" className="rt-cta-pink inline-flex items-center gap-2 h-11 px-6 text-sm mt-6">
          Open the Plan Builder <ArrowRight className="h-4 w-4" />
        </a>
      </div>
    </section>
  );
}
